import apiService from './api';
import {
  LoginRequest,
  LoginResponse,
  TwoFactorSetupRequest,
  TwoFactorVerifyRequest,
  EmailVerificationRequest,
  BackupCodeRequest,
  TwoFactorStatus,
  UserProfile
} from '../types';

class AuthService {
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';

  // Méthode de connexion principale
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiService.post('/auth/login/', credentials);
      const responseData = response.data;
      
      // Vérifier que la connexion a réussi
      if (responseData.success && responseData.data?.access_token) {
        const { access_token, refresh_token } = responseData.data;
        
        // Sauvegarder les tokens
        this.setAccessToken(access_token);
        this.setRefreshToken(refresh_token);
        
        return responseData;
      } else {
        throw new Error(responseData.message || 'Erreur de connexion');
      }
    } catch (error: any) {
      console.error('❌ Erreur lors de la connexion:', error);
      throw error;
    }
  }

  // Méthodes de gestion des tokens
  setAccessToken(token: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  getAccessToken(): string | null {
    const token = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    return token;
  }

  getRefreshToken(): string | null {
    const token = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    return token;
  }

  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY); // ← Corriger REFRESHToken_KEY en REFRESH_TOKEN_KEY
  }

  // Méthodes 2FA
  async verifyTwoFactor(data: TwoFactorVerifyRequest): Promise<any> {
    const response = await apiService.post('/auth/verify-two-factor/', data);
    return response.data;
  }

  async resendVerificationCode(data: EmailVerificationRequest): Promise<any> {
    const response = await apiService.post('/auth/resend-verification-code/', data);
    return response.data;
  }

  async setupTwoFactor(data: TwoFactorSetupRequest): Promise<any> {
    const response = await apiService.post('/auth/setup-two-factor/', data);
    return response.data;
  }

  async verifyTotpCode(data: TwoFactorVerifyRequest): Promise<any> {
    const response = await apiService.post('/auth/verify-totp-code/', data);
    return response.data;
  }

  async verifyBackupCode(data: BackupCodeRequest): Promise<any> {
    const response = await apiService.post('/auth/verify-backup-code/', data);
    return response.data;
  }

  async disableTwoFactor(): Promise<any> {
    const response = await apiService.post('/auth/disable-two-factor/', {});
    return response.data;
  }

  async getTwoFactorStatus(): Promise<TwoFactorStatus> {
    const response = await apiService.get('/auth/two-factor-status/');
    return response.data.data;
  }

  async getUserProfile(): Promise<UserProfile> {
    try {
      const token = this.getAccessToken();
      if (!token) {
        throw new Error('Aucun token d\'accès disponible');
      }
      
      // Récupérer le profil de base depuis l'API auth
      const response = await apiService.get('/auth/user-profile/');
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur lors de la récupération du profil');
      }
      
      let profileData = response.data.data;
      
      return profileData;
      
    } catch (error: any) {
      console.error('❌ Erreur dans getUserProfile:', error);
      throw error;
    }
  }

  async refreshToken(): Promise<any> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('Aucun refresh token disponible');
    }
    
    const response = await apiService.post('/auth/refresh/', { refresh: refreshToken });
    return response.data;
  }

  async changePassword(data: { current_password: string; new_password: string; confirm_password: string }): Promise<any> {
    const response = await apiService.post('/auth/change-password/', data);
    return response.data;
  }

  async forgotPassword(email: string): Promise<any> {
    const response = await apiService.post('/auth/forgot-password/', { email });
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        try {
          await apiService.post('/auth/logout/', { refresh_token: refreshToken });
        } catch (error) {
          // Même si l'appel API échoue, on continue le nettoyage local
          console.warn('Erreur lors de l\'appel API de déconnexion (nettoyage local continué):', error);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Toujours nettoyer les tokens localement, même si l'appel API échoue
      this.clearTokens();
      
      // Nettoyer aussi sessionStorage
      try {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('skip-login-redirect');
        sessionStorage.removeItem('activeSiteCode');
        sessionStorage.removeItem('activeSiteInfo');
      } catch (error) {
        console.warn('Erreur lors du nettoyage de sessionStorage:', error);
      }
      
      // Nettoyer aussi les autres données liées à l'authentification
      try {
        localStorage.removeItem('activeSiteCode');
        localStorage.removeItem('activeSiteInfo');
      } catch (error) {
        console.warn('Erreur lors du nettoyage des données de site:', error);
      }
    }
  }

  // Vérifier si l'utilisateur est connecté
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return token !== null;
  }
}

const authService = new AuthService();
export default authService;